import type { Availability } from "@/api/agents";
import { AVAILABILITY_HELP, AVAILABILITY_LABEL, AVAILABILITY_VALUES } from "@/api/agents";
import type { QueueDoc, QueueStrategy, QueueTier } from "@/api/queues";
import { STRATEGY_ALIAS } from "@/api/queues";

/**
 * The pure parts of the call center screens: the queue form's model, the
 * tier list, and what a person may set an agent's availability to. No
 * React and no fetching, so the rules can be read and tested on their own.
 */

/* --------------------------------------------------------------- the form */

/**
 * Numbers are held as strings so an empty box means "the server's
 * default" rather than zero, which is a real value for most of them
 * (`max_wait_time: 0` is "no limit").
 */
export interface QueueFormValues {
  name: string;
  strategy: string;
  ring_simultaneously: string;
  agent_ring_timeout: string;
  agent_wrapup_time: string;
  agent_no_answer_status: Availability;
  skip_agents_with_external_calls: boolean;
  time_base_score: "system" | "queue";
  greeting: string;
  moh: string;
  announce_media: string;
  announce_interval: string;
  announce_position: boolean;
  exit_keys: string;
  max_wait_time: string;
  max_wait_time_with_no_agent: string;
  max_wait_time_with_no_agent_time_reached: string;
  enter_when_empty: boolean;
  max_queue_size: string;
  tier_rules_apply: boolean;
  tier_rules_wait_seconds: string;
  tier_rules_wait_multiply_level: boolean;
  tier_rules_no_agent_no_wait: boolean;
  record: boolean;
  cid_prefix: string;
  outbound_caller_id_name: string;
  outbound_caller_id_number: string;
}

const numText = (n: unknown): string => (typeof n === "number" && Number.isFinite(n) ? String(n) : "");
const text = (s: unknown): string => (typeof s === "string" ? s : "");
const flag = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);

/** The defaults `queues.json` gives a document the server has just created. */
export const QUEUE_DEFAULTS: QueueFormValues = {
  name: "",
  strategy: "longest_idle",
  ring_simultaneously: "",
  agent_ring_timeout: "",
  agent_wrapup_time: "",
  agent_no_answer_status: "on_break",
  skip_agents_with_external_calls: true,
  time_base_score: "system",
  greeting: "",
  moh: "",
  announce_media: "",
  announce_interval: "",
  announce_position: false,
  exit_keys: "#",
  max_wait_time: "",
  max_wait_time_with_no_agent: "",
  max_wait_time_with_no_agent_time_reached: "",
  enter_when_empty: true,
  max_queue_size: "",
  tier_rules_apply: false,
  tier_rules_wait_seconds: "",
  tier_rules_wait_multiply_level: false,
  tier_rules_no_agent_no_wait: true,
  record: false,
  cid_prefix: "",
  outbound_caller_id_name: "",
  outbound_caller_id_number: "",
};

export function queueFormValues(doc?: QueueDoc): QueueFormValues {
  if (!doc) return { ...QUEUE_DEFAULTS };
  return {
    name: text(doc.name),
    strategy: text(doc.strategy) || "longest_idle",
    ring_simultaneously: numText(doc.ring_simultaneously),
    agent_ring_timeout: numText(doc.agent_ring_timeout),
    agent_wrapup_time: numText(doc.agent_wrapup_time),
    agent_no_answer_status: (doc.agent_no_answer_status ?? "on_break") as Availability,
    skip_agents_with_external_calls: flag(doc.skip_agents_with_external_calls, true),
    time_base_score: doc.time_base_score === "queue" ? "queue" : "system",
    greeting: text(doc.greeting),
    moh: text(doc.moh),
    announce_media: text(doc.announce?.media),
    announce_interval: numText(doc.announce?.interval),
    announce_position: flag(doc.announce?.position, false),
    // `caller_exit_key` is the one-key form this replaces: it is read so an
    // older document keeps its key, and never written back.
    exit_keys: text(doc.exit_keys) || text(doc.caller_exit_key) || "#",
    max_wait_time: numText(doc.max_wait_time),
    max_wait_time_with_no_agent: numText(doc.max_wait_time_with_no_agent),
    max_wait_time_with_no_agent_time_reached: numText(doc.max_wait_time_with_no_agent_time_reached),
    enter_when_empty: flag(doc.enter_when_empty, true),
    max_queue_size: numText(doc.max_queue_size),
    tier_rules_apply: flag(doc.tier_rules?.apply, false),
    tier_rules_wait_seconds: numText(doc.tier_rules?.wait_seconds),
    tier_rules_wait_multiply_level: flag(doc.tier_rules?.wait_multiply_level, false),
    tier_rules_no_agent_no_wait: flag(doc.tier_rules?.no_agent_no_wait, true),
    record: flag(doc.record, false),
    cid_prefix: text(doc.cid_prefix),
    outbound_caller_id_name: text(doc.outbound_caller_id?.name),
    outbound_caller_id_number: text(doc.outbound_caller_id?.number),
  };
}

/** A field left empty is dropped, so the document keeps only what was chosen. */
const int = (s: string): number | undefined => {
  const t = s.trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
};

const object = <T extends Record<string, unknown>>(o: T): T | undefined => (Object.values(o).some((v) => v !== undefined) ? o : undefined);

/** Drop the keys whose value is undefined, so a PUT body carries nothing empty. */
function compact<T extends Record<string, unknown>>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) out[k] = v;
  return out as T;
}

/**
 * Keys `queues.json` no longer has. Nothing on the server ever read them;
 * an older document may still carry them, so they are tolerated on load
 * and never written, and a patch leaves them where they are.
 */
export const DROPPED_QUEUE_KEYS: readonly string[] = [
  "connection_timeout",
  "abandoned_resume_allowed",
  "discard_abandoned_after",
  "record_caller",
  "recording_url",
  "notification_email",
  "cdr_url",
  "max_priority",
];

/**
 * The document to write. Everything the form does not know about is kept
 * from `doc` (the `announcements` prompts, whatever a later version adds).
 * `agents` is kept too: tiers are written through their own resource.
 * Keys `queues.json` has dropped are not: nothing on the server reads them.
 */
export function queueFormToDoc(values: QueueFormValues, doc?: QueueDoc): QueueDoc {
  const {
    name: _name,
    strategy: _strategy,
    announce: _announce,
    tier_rules: _tierRules,
    outbound_caller_id: _cid,
    caller_exit_key: _exitKey,
    ...kept
  } = (doc ?? {}) as QueueDoc;
  const rest = Object.fromEntries(Object.entries(kept).filter(([k]) => !DROPPED_QUEUE_KEYS.includes(k)));
  const announce = object({
    media: values.announce_media || undefined,
    interval: int(values.announce_interval),
    position: values.announce_position || undefined,
  });
  const tierRules = object({
    apply: values.tier_rules_apply || undefined,
    wait_seconds: int(values.tier_rules_wait_seconds),
    wait_multiply_level: values.tier_rules_wait_multiply_level || undefined,
    // The schema default is true, so only the switched-off case is written.
    no_agent_no_wait: values.tier_rules_no_agent_no_wait ? undefined : false,
  });
  const outbound = object({ name: values.outbound_caller_id_name || undefined, number: values.outbound_caller_id_number || undefined });

  return compact({
    ...rest,
    name: values.name.trim(),
    strategy: values.strategy as QueueStrategy,
    ring_simultaneously: int(values.ring_simultaneously),
    agent_ring_timeout: int(values.agent_ring_timeout),
    agent_wrapup_time: int(values.agent_wrapup_time),
    agent_no_answer_status: values.agent_no_answer_status,
    skip_agents_with_external_calls: values.skip_agents_with_external_calls,
    time_base_score: values.time_base_score,
    greeting: values.greeting || undefined,
    moh: values.moh || undefined,
    announce,
    exit_keys: values.exit_keys,
    max_wait_time: int(values.max_wait_time),
    max_wait_time_with_no_agent: int(values.max_wait_time_with_no_agent),
    max_wait_time_with_no_agent_time_reached: int(values.max_wait_time_with_no_agent_time_reached),
    enter_when_empty: values.enter_when_empty,
    max_queue_size: int(values.max_queue_size),
    tier_rules: tierRules,
    record: values.record,
    cid_prefix: values.cid_prefix || undefined,
    outbound_caller_id: outbound,
  }) as QueueDoc;
}

/** The strategy a document carries that the picker does not offer, if any. */
export const aliasStrategy = (doc: QueueDoc | undefined): boolean => doc?.strategy === STRATEGY_ALIAS;

/** The top-level keys the form owns; everything else on the document is left alone. */
export const QUEUE_FORM_KEYS = [
  "name",
  "strategy",
  "ring_simultaneously",
  "agent_ring_timeout",
  "agent_wrapup_time",
  "agent_no_answer_status",
  "skip_agents_with_external_calls",
  "time_base_score",
  "greeting",
  "moh",
  "announce",
  "exit_keys",
  "max_wait_time",
  "max_wait_time_with_no_agent",
  "max_wait_time_with_no_agent_time_reached",
  "enter_when_empty",
  "max_queue_size",
  "tier_rules",
  "record",
  "cid_prefix",
  "outbound_caller_id",
] as const;

/**
 * What `queues.json` gives a key that is absent. The form always has a
 * value for these, so a patch would otherwise write the default onto every
 * document that had simply left the key out.
 */
const SCHEMA_DEFAULTS: Record<string, unknown> = {
  strategy: "longest_idle",
  agent_no_answer_status: "on_break",
  skip_agents_with_external_calls: true,
  time_base_score: "system",
  exit_keys: "#",
  enter_when_empty: true,
  record: false,
};

const isPlainObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** One level of nesting: a sub-key that has gone is `null`, since PATCH merges rather than replaces. */
function objectPatch(before: unknown, after: Record<string, unknown>): Record<string, unknown> | undefined {
  const was = isPlainObject(before) ? before : {};
  const patch: Record<string, unknown> = {};
  for (const key of new Set([...Object.keys(was), ...Object.keys(after)])) {
    const a = after[key];
    if (a === undefined) {
      if (was[key] !== undefined) patch[key] = null;
    } else if (a !== was[key]) {
      patch[key] = a;
    }
  }
  return Object.keys(patch).length ? patch : undefined;
}

/**
 * An edit is a PATCH of what changed, never a POST of the loaded
 * document: `agents` is written through its own resource, and a full
 * document would put back whatever tier list this form last saw.
 */
export function queuePatch(before: QueueDoc | undefined, values: QueueFormValues): Record<string, unknown> {
  const after = queueFormToDoc(values, before) as Record<string, unknown>;
  const was = (before ?? {}) as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  for (const key of QUEUE_FORM_KEYS) {
    const a = after[key];
    if (a === undefined) {
      if (was[key] !== undefined) patch[key] = null;
      continue;
    }
    if (isPlainObject(a)) {
      const sub = objectPatch(was[key], a);
      if (sub) patch[key] = sub;
      continue;
    }
    // A key the document leaves out, still holding the value the schema
    // would give it, is left out.
    if (was[key] === undefined && a === SCHEMA_DEFAULTS[key]) continue;
    if (a !== was[key]) patch[key] = a;
  }
  // The one-key form is replaced by `exit_keys`, which the form always writes.
  if (was.caller_exit_key !== undefined) patch.caller_exit_key = null;
  return patch;
}

/* --------------------------------------------------------------- the tiers */

export interface TierDraft {
  id: string;
  level: number;
  position: number;
}

export const tierOf = (t: QueueTier): TierDraft => ({ id: t.id, level: t.level ?? 1, position: t.position ?? 1 });

/** Level first, then position, then the id so the order never wobbles. */
export function sortTiers(list: TierDraft[]): TierDraft[] {
  return [...list].sort((a, b) => a.level - b.level || a.position - b.position || a.id.localeCompare(b.id));
}

/** Positions 1..n within each level, in the order the list is already in. */
export function normaliseTiers(list: TierDraft[]): TierDraft[] {
  const seen = new Map<number, number>();
  return sortTiers(list).map((t) => {
    const next = (seen.get(t.level) ?? 0) + 1;
    seen.set(t.level, next);
    return t.position === next ? t : { ...t, position: next };
  });
}

/** Adds the agent at the end of the level, or leaves the list alone if they are already in it. */
export function addTier(list: TierDraft[], id: string, level = 1): TierDraft[] {
  if (list.some((t) => t.id === id)) return list;
  const last = list.filter((t) => t.level === level).reduce((max, t) => Math.max(max, t.position), 0);
  return normaliseTiers([...list, { id, level, position: last + 1 }]);
}

export function removeTier(list: TierDraft[], id: string): TierDraft[] {
  const next = list.filter((t) => t.id !== id);
  return next.length === list.length ? list : normaliseTiers(next);
}

/** Moving an agent to another level puts them at the end of it. */
export function setTierLevel(list: TierDraft[], id: string, level: number): TierDraft[] {
  const target = Math.max(1, Math.trunc(level) || 1);
  const current = list.find((t) => t.id === id);
  if (!current || current.level === target) return list;
  const last = list.filter((t) => t.level === target && t.id !== id).reduce((max, t) => Math.max(max, t.position), 0);
  return normaliseTiers(list.map((t) => (t.id === id ? { ...t, level: target, position: last + 1 } : t)));
}

/** Moving within a level: everyone at or after the new position shifts down. */
export function setTierPosition(list: TierDraft[], id: string, position: number): TierDraft[] {
  const current = list.find((t) => t.id === id);
  if (!current) return list;
  const peers = sortTiers(list.filter((t) => t.level === current.level));
  const target = Math.min(Math.max(1, Math.trunc(position) || 1), peers.length);
  const without = peers.filter((t) => t.id !== id);
  without.splice(target - 1, 0, current);
  const renumbered = without.map((t, i) => ({ ...t, position: i + 1 }));
  const others = list.filter((t) => t.level !== current.level);
  return sortTiers([...others, ...renumbered]);
}

/** The levels in use, so the editor can group without inventing empty ones. */
export const tierLevels = (list: TierDraft[]): number[] => [...new Set(list.map((t) => t.level))].sort((a, b) => a - b);

/**
 * What one live tier edit costs in requests: the sub-resource takes one
 * agent at a time, so a move that renumbers a level writes every row it
 * moved. `before` and `after` are the whole list.
 */
export function tierWrites(before: TierDraft[], after: TierDraft[]): { add: TierDraft[]; change: TierDraft[]; remove: string[] } {
  const was = new Map(before.map((t) => [t.id, t]));
  const now = new Map(after.map((t) => [t.id, t]));
  return {
    add: after.filter((t) => !was.has(t.id)),
    change: after.filter((t) => {
      const old = was.get(t.id);
      return old !== undefined && (old.level !== t.level || old.position !== t.position);
    }),
    remove: before.filter((t) => !now.has(t.id)).map((t) => t.id),
  };
}

/* -------------------------------------------------------- availability */

export interface AvailabilityChoice {
  value: Availability;
  label: string;
  help: string;
  disabled?: boolean;
}

/**
 * Do-not-disturb on the user holds the agent at logged out and restores
 * what they had when it is cleared, so the control is not offered while it
 * is on; this is the sentence that says why.
 */
export const availabilityBlocked = (dndEnabled: boolean | undefined): string | undefined =>
  dndEnabled ? "Do not disturb is on for this user, so they are held logged out. Clearing it restores the availability they had." : undefined;

/**
 * The values a person may set now: all four, minus the one already set.
 * A disabled user may only be logged out; the server refuses the rest.
 */
export function availabilityChoices(current: Availability | undefined, opts: { dndEnabled?: boolean; userDisabled?: boolean } = {}): AvailabilityChoice[] {
  const blocked = Boolean(opts.dndEnabled);
  return AVAILABILITY_VALUES.map((value) => ({
    value,
    label: AVAILABILITY_LABEL[value],
    help: AVAILABILITY_HELP[value],
    disabled: blocked || value === current || (Boolean(opts.userDisabled) && value !== "logged_out"),
  }));
}

/** `ui` when someone changes their own, `supervisor` when they change another agent's. */
export const availabilitySource = (isSelf: boolean): "ui" | "supervisor" => (isSelf ? "ui" : "supervisor");

/** The dot beside an agent: available and idle is good, on a call is busy, logged out is off. */
export function agentDot(availability: Availability | undefined, activity?: string): "ok" | "warn" | "bad" | "off" {
  if (!availability || availability === "logged_out") return "off";
  if (availability === "on_break") return "warn";
  if (activity === "on_call" || activity === "ringing" || activity === "wrapping_up") return "warn";
  return "ok";
}
